import type { BookedSpaceCalendarView } from "../../types/BookedSpaceCalendarView";

type SpaceUsageSource = Pick<
    BookedSpaceCalendarView,
    | 'calendarDate'
    | 'totalSpacesBooked'
    | 'maxInventoryCount'
    | 'overageSpacesChargedCount'
    | 'locationName'
    | 'buyerCompanyName'
    | 'supplierCompanyName'
    | 'overageInvoiceNumbers'
    | 'bookingNumbers'
>;

interface FormattedSpaceUsageBase {
    date: string;
    bookedSpaces: number;
    maxInventory: number;
    overageSpaces: number;
    invoicedOverages: number;
    unchargedOverages: number;
    location: string;
    overageInvoices: string;
    bookings: string;
}

export type FormattedSupplierSpaceUsageItem = FormattedSpaceUsageBase & { buyer: string };
export type FormattedBuyerSpaceUsageItem = FormattedSpaceUsageBase & { supplier: string };

export function formatSpaceUsageItem(spaceUsageItem: SpaceUsageSource, isSupplier: true): FormattedSupplierSpaceUsageItem;
export function formatSpaceUsageItem(spaceUsageItem: SpaceUsageSource, isSupplier?: false): FormattedBuyerSpaceUsageItem;
export function formatSpaceUsageItem(spaceUsageItem: SpaceUsageSource, isSupplier?: boolean): FormattedSupplierSpaceUsageItem | FormattedBuyerSpaceUsageItem;
export function formatSpaceUsageItem(spaceUsageItem: SpaceUsageSource, isSupplier?: boolean): FormattedSupplierSpaceUsageItem | FormattedBuyerSpaceUsageItem {

    const {
      calendarDate,
      totalSpacesBooked,
      maxInventoryCount,
      overageSpacesChargedCount,
      locationName,
      buyerCompanyName,
      supplierCompanyName,
      overageInvoiceNumbers,
      bookingNumbers
    } = spaceUsageItem;

    const calculateOveragesSpaces = (): number => {
        return maxInventoryCount > totalSpacesBooked ? maxInventoryCount - totalSpacesBooked : 0;
    };

    const calculateUnchargedOverages = (): number => {
        return calculateOveragesSpaces() - overageSpacesChargedCount;
    };

    const prefix = {
        date: calendarDate,
        bookedSpaces: totalSpacesBooked,
        maxInventory: maxInventoryCount,
        overageSpaces: calculateOveragesSpaces(),
        invoicedOverages: overageSpacesChargedCount,
        unchargedOverages: calculateUnchargedOverages(),
        location: locationName,
    }

    const suffix = {
        overageInvoices: overageInvoiceNumbers,
        bookings: totalSpacesBooked > 0 ? bookingNumbers : '',
    }

    if (isSupplier) {
        return {
            ...prefix,
            buyer: buyerCompanyName,
            ...suffix,
        }
    }

    return {
        ...prefix,
        supplier: supplierCompanyName,
        ...suffix,
    };
}

export const sortByFields: Array<{ label: string; value: string }> = [
    {label: "Supplier", value: "SUPPLIER_COMPANY_NAME"},
    {label: "Bookings", value: "BOOKING_NUMBERS"},
    {label: "Location", value: "LOCATION_NAME"},
    {label: "Overage Invoices", value: "OVERAGE_INVOICE_NUMBERS"},
    {label: "Booked Spaces", value: "TOTAL_SPACES_BOOKED"},
    {label: "Date", value: "CALENDAR_DATE"},
    {label: "Max Inventory", value: "MAX_INVENTORY_COUNT"},
    {label: "Charged Overages", value: "OVERAGE_SPACES_CHARGED_COUNT"},
];
